import {
  type AnnualPlannerUnavailableKey,
} from '../annual-planner-access';
import { useTranslation } from 'react-i18next';

type AnnualPlannerUnavailableStateProps = {
  /** Which half of the planner is blocked — selects the title/subtitle pair. */
  stateKey: AnnualPlannerUnavailableKey;
  /**
   * `pane` replaces the whole planner content (neither View granted);
   * `inline` fills a single section (tracks column or blocks timeline) while
   * the rest of the planner keeps rendering.
   */
  variant: 'pane' | 'inline';
};

/**
 * A permission empty state for the Annual Planner. Rendered as a status so
 * the planner never silently disappears for assistive tech.
 */
export function AnnualPlannerUnavailableState({
  stateKey,
  variant,
}: AnnualPlannerUnavailableStateProps) {
  const { t } = useTranslation();

  // Literal `t()` calls (not a dynamic key) so the i18n extractor sees them.
  const messages: Record<
    AnnualPlannerUnavailableKey,
    { title: string; body: string }
  > = {
    unableToDisplayTracksOrBlocks: {
      title: t('unableToDisplayTracksOrBlocks'),
      body: t('unableToDisplayTracksOrBlocksBody'),
    },
    unableToDisplayBlocks: {
      title: t('unableToDisplayBlocks'),
      body: t('unableToDisplayBlocksBody'),
    },
    unableToDisplayTracks: {
      title: t('unableToDisplayTracks'),
      body: t('unableToDisplayTracksBody'),
    },
  };

  const { title, body } = messages[stateKey];

  return (
    <div
      role="status"
      className={
        variant === 'pane'
          ? 'h-full w-full flex flex-col items-center justify-center gap-2 p-6 text-center'
          : 'h-full w-full flex flex-col items-center justify-center gap-1 p-4 text-center'
      }
    >
      <span
        className={
          variant === 'pane'
            ? 'font-semibold text-base text-gray-900'
            : 'font-semibold text-sm text-gray-900'
        }
      >
        {title}
      </span>
      <span className={variant === 'pane' ? 'text-sm text-gray-600' : 'text-xs text-gray-600'}>
        {body}
      </span>
    </div>
  );
}
